// Tabs on the company and contact pages.
//
// Those pages keep two things that must stay in step: the tabs they draw, and the list of tab
// keys that turns a clicked tab's position into the `tab` part of the URL (and back). They went
// out of step whenever a custom-entity relationship was hidden from the viewer: the drawn tabs
// skipped it, the key list did not, and every tab after it pointed one place off. Clicking
// "Kontakter agentnr" then put `kompetanse` in the URL and the tab spun forever waiting for the
// wrong entity's data.
//
// Both now come from the functions below, so they cannot disagree. Plain functions with no Vue
// or app imports, so they can be tested on their own (tests/unit/entityTabs.test.js).

/**
 * Whether the viewer may see a relationship tab.
 *
 * `visibility2` is the visibility set on the related entity: 'all', 'admin' (company admins only),
 * 'group' (members of `visibleToGroup2` only) or 'hide'. Anything else — including no value, which
 * older entities have — is not shown; that was already the behaviour of the drawn tabs.
 *
 * @param {{visibility2?: string, visibleToGroup2?: number}} relationship
 * @param {{isCompanyAdmin: boolean, groupIds: number[]}} viewer
 */
export function isRelationshipVisible(relationship, viewer) {
  switch (relationship.visibility2) {
    case 'all':
      return true
    case 'admin':
      return Boolean(viewer.isCompanyAdmin)
    case 'group':
      return (viewer.groupIds || []).includes(relationship.visibleToGroup2)
    default:
      return false
  }
}

/** The relationships the viewer may see, in the order given. */
export function visibleRelationships(relationships, viewer) {
  return (relationships || []).filter(relationship => isRelationshipVisible(relationship, viewer))
}

/**
 * The tab keys in the order the tabs are drawn: the fixed tabs the viewer may see, then one per
 * visible relationship.
 *
 * @param {Array<{key: string, permission?: [string, string]}>} fixedTabs in template order; a tab
 *        with a `permission` is drawn only when `can(...permission)` is true, as its v-if does
 * @param {Function} can the page's `$can`
 * @param {Array<{key2: string}>} relationships already filtered to the visible ones
 */
export function tabKeys(fixedTabs, can, relationships) {
  return [
    ...fixedTabs.filter(tab => !tab.permission || can(...tab.permission)).map(tab => tab.key),
    ...(relationships || []).map(relationship => relationship.key2),
  ]
}
